import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { fmtINR } from "../fmt";

interface CategoryTotal {
  categoryId: string;
  name: string;
  total: number;
}
interface PnLResult {
  income: CategoryTotal[];
  expense: CategoryTotal[];
  net: number;
}

export function PnL() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [result, setResult] = useState<PnLResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    const suffix = qs.toString() ? `?${qs}` : "";
    api
      .get<PnLResult>(`/api/pnl${suffix}`)
      .then(setResult)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load P&L"));
  }, [from, to]);

  const incomeTotal = result?.income.reduce((s, r) => s + r.total, 0) ?? 0;
  const expenseTotal = result?.expense.reduce((s, r) => s + r.total, 0) ?? 0;

  return (
    <div>
      <h1>Profit &amp; Loss</h1>
      {error && <div className="error-text">{error}</div>}

      <div className="toolbar">
        <div className="field">
          <label htmlFor="p-from">From</label>
          <input id="p-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="p-to">To</label>
          <input id="p-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      <div className="card-row" style={{ marginBottom: "1rem" }}>
        <div className="card stat-card">
          <div className="label">Income</div>
          <div className="value">{fmtINR(incomeTotal)}</div>
        </div>
        <div className="card stat-card">
          <div className="label">Expense</div>
          <div className="value">{fmtINR(expenseTotal)}</div>
        </div>
        <div className="card stat-card">
          <div className="label">Net</div>
          <div className="value">{result ? fmtINR(result.net) : "..."}</div>
        </div>
      </div>

      <div className="card-row">
        <div style={{ flex: 1, minWidth: 280 }}>
          <h2>Income by category</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {(result?.income ?? []).map((r) => (
                  <tr key={r.categoryId}>
                    <td>{r.name}</td>
                    <td className="num">{fmtINR(r.total)}</td>
                  </tr>
                ))}
                {result && result.income.length === 0 && (
                  <tr>
                    <td colSpan={2} className="empty-state">
                      No income in this range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <h2>Expense by category</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {(result?.expense ?? []).map((r) => (
                  <tr key={r.categoryId}>
                    <td>{r.name}</td>
                    <td className="num">{fmtINR(r.total)}</td>
                  </tr>
                ))}
                {result && result.expense.length === 0 && (
                  <tr>
                    <td colSpan={2} className="empty-state">
                      No expense in this range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
